/**
 * PhotoGrid — renders a gallery's photographs as a responsive grid or a
 * horizontal scroll-snap strip, and owns the shared lightbox.
 *
 * Adapted from the lecture gallery components that shipped in 19dddb1
 * (`SectionImages.tsx` + `ImageSlider.tsx`). The layout decisions carry over:
 *
 *   - `grid` is the default; a slider is the deliberate choice, so the quiet
 *     fallback is the grid.
 *   - the slider is CSS scroll-snap, NOT a JS carousel. This site prerenders
 *     every page to static HTML, and a JS-driven carousel renders its slides
 *     only after hydration — the static document would show an empty box.
 *     Scroll-snap needs no JavaScript at all, and native touch/trackpad/
 *     keyboard scrolling comes for free.
 *   - one lightbox per gallery, in BOTH layouts: a thumbnail is a thumbnail,
 *     and a group photograph is unreadable at 50% of a phone's width.
 *
 * WHAT CHANGED IN THE ADAPTATION
 * ------------------------------
 * Photographs are media uuids now, not server-resolved `{url, width, height,
 * alt}` objects. Thumbnails therefore go through the shared <CmsImage>, which
 * resolves the `srcset`, native size and locale-correct alt from
 * `cmsMediaIndex` — the media library owns the alt text, so no custom `alt`
 * is passed here.
 *
 * A uuid that is not in the generated index (a fresh deploy before cms-sync, a
 * deleted media row) is skipped rather than rendered as a broken image.
 */
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import CmsImage from '@/components/media/CmsImage'
import { cmsMediaIndex } from '@/content/cms/media.generated'
import { cmsMediaLargestWebpUrl } from '@/utils/cmsMediaUrl'
import GalleryLightbox from './GalleryLightbox'

export interface GalleryPhotosProps {
  /** {uuid, caption?}, in display order. */
  photos: Array<{ uuid: string; caption?: string }>
  /** 'grid' (default) | 'slider' (scroll-snap strip) | 'list' (single column). */
  layout?: 'grid' | 'slider' | 'list'
  className?: string
}

/**
 * The layout width hints.
 *
 * A grid column is ~50vw (two-up) on phones, ~33vw on tablets and ~25vw in the
 * four-up desktop grid. The slider shows one slide at a time: 80vw on phones,
 * a fixed 320px from 640px up.
 */
const GRID_SIZES = '(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 25vw'
const SLIDER_SIZES = '(max-width: 640px) 80vw, 320px'

/** The shared per-photo frame: rounded, bordered and shadowed like the rest
 *  of the site's media. The grid crops to a uniform 4:3 so rows stay even;
 *  the slider keeps each photo's own aspect ratio. */
const GRID_IMAGE_CLASS =
  'block w-full aspect-[4/3] object-cover rounded-yada-md md:rounded-yada-lg border border-border shadow-yada'
const SLIDER_IMAGE_CLASS =
  'block w-full h-auto rounded-yada-md md:rounded-yada-lg border border-border shadow-yada'

export function GalleryPhotos({
  photos,
  layout = 'grid',
  className,
}: GalleryPhotosProps) {
  const { t } = useTranslation()

  /**
   * Which photo the lightbox is showing, and which thumbnail opened it.
   * `null` means closed. The state lives here, above both layouts, so the grid
   * and the slider share one preview rather than behaving differently.
   */
  const [preview, setPreview] = useState<{
    index: number
    trigger: HTMLElement | null
  } | null>(null)

  // Keep only photos the media index can actually serve. Filtering here means
  // the lightbox's index always agrees with the thumbnails' index.
  const valid = photos.filter(
    (p) => cmsMediaIndex[p.uuid] && cmsMediaLargestWebpUrl(p.uuid),
  )

  if (valid.length === 0) return null

  const openPreview = (index: number, trigger: HTMLElement) => {
    setPreview({ index, trigger })
  }

  const lightbox = (
    <GalleryLightbox
      photos={valid}
      index={preview?.index ?? null}
      onIndexChange={(index) =>
        setPreview((current) => (current ? { ...current, index } : current))
      }
      onClose={() => setPreview(null)}
      trigger={preview?.trigger ?? null}
    />
  )

  if (layout === 'slider') {
    return (
      <>
        <div
          className={className ?? 'my-7'}
          role="group"
          aria-roledescription="carousel"
          aria-label={t('gallery.sliderLabel')}
        >
          {/* `snap-x snap-mandatory` on the track + `snap-center` on the
              slides is the whole carousel: the browser snaps the nearest
              slide into the centre by itself, with no JavaScript involved.
              The next slide peeks in from the edge — the only "there is more"
              cue a scroll container gives on touch devices. */}
          <div className="relative flex gap-4 overflow-x-auto overflow-y-hidden snap-x snap-mandatory scroll-smooth motion-reduce:scroll-auto scrollbar-none [&::-webkit-scrollbar]:hidden pb-1">
            {valid.map((p, index) => (
              <figure
                key={p.uuid}
                role="group"
                aria-roledescription="slide"
                aria-label={t('gallery.lightbox.counter', {
                  current: index + 1,
                  total: valid.length,
                })}
                className="snap-center shrink-0 w-[80vw] sm:w-[320px]"
              >
                <button
                  type="button"
                  onClick={(event) => openPreview(index, event.currentTarget)}
                  aria-label={t('gallery.lightbox.open')}
                  className="block w-full cursor-zoom-in focus-visible:outline-2 focus-visible:outline-primary focus-visible:outline-offset-4 rounded-yada-md md:rounded-yada-lg"
                >
                  <CmsImage uuid={p.uuid} sizes={SLIDER_SIZES} className={SLIDER_IMAGE_CLASS} />
                </button>
              </figure>
            ))}
          </div>
        </div>
        {lightbox}
      </>
    )
  }

  if (layout === 'list') {
    return (
      <>
        <div data-gallery-list className={className ?? 'my-7'}>
          {valid.map((p, index) => (
            <figure
              key={p.uuid}
              className="flex flex-col items-start gap-2 mb-10"
            >
              <button
                type="button"
                onClick={(event) => openPreview(index, event.currentTarget)}
                aria-label={t('gallery.lightbox.open')}
                className="block w-full cursor-zoom-in focus-visible:outline-2 focus-visible:outline-primary focus-visible:outline-offset-4 rounded-yada-lg"
              >
                <CmsImage uuid={p.uuid} sizes="100vw" className="w-full h-auto rounded-yada-lg border border-border shadow-yada" />
              </button>
              {p.caption && (
                <figcaption className="text-sm text-ink-muted max-w-2xl">
                  {p.caption}
                </figcaption>
              )}
              <span className="text-[11px] text-ink-muted">
                {t('gallery.lightbox.counter', { current: index + 1, total: valid.length })}
              </span>
            </figure>
          ))}
        </div>
        {lightbox}
      </>
    )
  }

  return (
    <>
      <div data-gallery-grid className={className ?? 'my-7'}>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 lg:gap-6">
          {valid.map((p, index) => (
            <figure key={p.uuid}>
              <button
                type="button"
                onClick={(event) => openPreview(index, event.currentTarget)}
                aria-label={t('gallery.lightbox.open')}
                className="block w-full cursor-zoom-in focus-visible:outline-2 focus-visible:outline-primary focus-visible:outline-offset-4 rounded-yada-md md:rounded-yada-lg"
              >
                <CmsImage uuid={p.uuid} sizes={GRID_SIZES} className={GRID_IMAGE_CLASS} />
              </button>
            </figure>
          ))}
        </div>
      </div>
      {lightbox}
    </>
  )
}

export default GalleryPhotos
